import { ObservableModelNode } from "./observable";
import { StyleModel } from "./StyleModel";
import { SPECS_DISPLAY_METHODS } from "./displayMethods";
import {
  cloneValue,
  expectObject,
  readBoolean,
  readObjectArray,
  readString,
  readStringEnum,
  splitKnown,
} from "./utils";
import type { JsonObject } from "./utils";
import type { SpecsDisplayMethod } from "../types";

export class ElementModel extends ObservableModelNode {
  name!: string;
  inherit!: boolean;
  inheritFromGroup!: string;
  defaultStyle!: string;
  styles!: StyleModel[];
  extra!: JsonObject;
  displayExtra!: JsonObject;
  displayable!: boolean;
  menuIconUrl!: string;
  displayMethod!: SpecsDisplayMethod;
  private hasInherit = false;
  private hasInheritFromGroup = false;
  private hasDisplayMethod = false;

  constructor() {
    super();
    this.defineObservableValue("name", "");
    this.defineObservableValue("inherit", false);
    this.defineObservableValue("inheritFromGroup", "");
    this.defineObservableValue("defaultStyle", "");
    this.defineObservableValue("styles", []);
    this.defineObservableValue("extra", {});
    this.defineObservableValue("displayExtra", {});
    this.defineObservableValue("displayable", true);
    this.defineObservableValue("menuIconUrl", "");
    this.defineObservableValue("displayMethod", "icon");
  }

  restore(data: unknown) {
    const source = expectObject(data, "element");
    const display = source.display
      ? expectObject(source.display, "element.display")
      : {};

    this.hasInherit = "inherit" in source;
    this.hasInheritFromGroup = "inheritFromGroup" in source;
    this.hasDisplayMethod = "displayMethod" in display;
    this.extra = splitKnown(source, [
      "name",
      "inherit",
      "inheritFromGroup",
      "defaultStyle",
      "styles",
      "display",
    ]);
    this.displayExtra = splitKnown(display, [
      "displayable",
      "menuIconUrl",
      "displayMethod",
    ]);
    this.name = readString(source, "name");
    this.inherit = readBoolean(source, "inherit", false);
    this.inheritFromGroup = readString(source, "inheritFromGroup");
    this.defaultStyle = readString(source, "defaultStyle");
    this.displayable = readBoolean(display, "displayable", true);
    this.menuIconUrl = readString(display, "menuIconUrl");
    this.displayMethod = readStringEnum(
      display,
      "displayMethod",
      SPECS_DISPLAY_METHODS,
      "icon"
    );
    this.styles = readObjectArray(source, "styles").map((style) =>
      new StyleModel().restore(style)
    );

    return this;
  }

  save() {
    return {
      ...cloneValue(this.extra),
      name: this.name,
      ...(this.hasInherit || this.inherit ? { inherit: this.inherit } : {}),
      ...(this.hasInheritFromGroup || this.inheritFromGroup
        ? { inheritFromGroup: this.inheritFromGroup }
        : {}),
      defaultStyle: this.defaultStyle,
      styles: this.styles.map((style) => style.save()),
      display: {
        ...cloneValue(this.displayExtra),
        displayable: this.displayable,
        menuIconUrl: this.menuIconUrl,
        ...(this.hasDisplayMethod || this.displayMethod !== "icon"
          ? { displayMethod: this.displayMethod }
          : {}),
      },
    };
  }

  addStyle(styleName: string) {
    if (!styleName || this.styles.some((style) => style.name === styleName)) {
      return false;
    }

    this.styles.push(
      new StyleModel().restore({
        name: styleName,
        defaultLayer: "",
        positionIds: [],
        layers: [],
        display: {
          displayable: true,
          menuIconUrl: "",
          displayMethod: "icon",
        },
      })
    );
    return true;
  }

  removeStyle(styleName: string) {
    if (this.styles.length <= 1) {
      return false;
    }

    const nextStyles = this.styles.filter((style) => style.name !== styleName);
    if (nextStyles.length === this.styles.length) {
      return false;
    }

    this.styles.splice(0, this.styles.length, ...nextStyles);
    if (this.defaultStyle === styleName) {
      this.defaultStyle = nextStyles[0]?.name || "";
    }
    return true;
  }

  renameStyle(previousName: string, nextName: string) {
    if (!nextName || previousName === nextName) {
      return false;
    }

    if (this.styles.some((style) => style.name === nextName)) {
      return false;
    }

    const style = this.styles.find((item) => item.name === previousName);
    if (!style) {
      return false;
    }

    style.name = nextName;
    if (this.defaultStyle === previousName) {
      this.defaultStyle = nextName;
    }
    return true;
  }

  setStyleIcon(styleName: string, menuIconUrl: string) {
    const style = this.styles.find((item) => item.name === styleName);
    if (!style) {
      return false;
    }

    style.menuIconUrl = menuIconUrl;
    return true;
  }

  setInheritance(inheritanceOptions: {
    inherit?: boolean;
    inheritFromGroup?: string;
  }) {
    if (inheritanceOptions.inherit !== undefined) {
      this.hasInherit = true;
      this.inherit = inheritanceOptions.inherit;
    }
    if (inheritanceOptions.inheritFromGroup !== undefined) {
      this.hasInheritFromGroup = !!inheritanceOptions.inheritFromGroup;
      this.inheritFromGroup = inheritanceOptions.inheritFromGroup;
    }
    return true;
  }

  getStyle(styleName: string) {
    return this.styles.find((style) => style.name === styleName) ?? null;
  }

  getDefaultStyle() {
    return this.getStyle(this.defaultStyle) ?? this.styles[0] ?? null;
  }

  getInheritanceGroups() {
    return this.styles.flatMap((style) => style.getInheritanceGroups());
  }
}
